import React, { useState } from 'react';
import { ACTIVITY_CATEGORIES, ActivityCategory } from '../../types/user';
import { useUser } from '../providers/UserProvider';
import { detectURLActivity } from '../../hooks/useActivityTracker';
import { useGamification } from '../../hooks/useGamification';

interface Props { onClose: () => void; }

export function ActivityPanel({ onClose }: Props) {
  const { user, updateUser } = useUser();
  const { updateStreak, addExperience } = useGamification();
  const [category, setCategory] = useState<ActivityCategory>(user?.activityCategory || 'other');
  const [text, setText]         = useState(user?.activity || '');
  const [urlHint, setUrlHint]   = useState<string | null>(null);

  const handleTextChange = (val: string) => {
    setText(val);
    const urlMatch = val.match(/https?:\/\/[^\s]+/);
    if (urlMatch) {
      const detected = detectURLActivity(urlMatch[0]);
      if (detected) {
        setUrlHint(`Detected: ${detected.text}`);
        setCategory(detected.category);
      }
    } else {
      setUrlHint(null);
    }
  };

  const save = () => {
    updateUser({ activity: text.trim(), activityCategory: category });
    const today = new Date().toISOString().split('T')[0];
    updateStreak(today);
    addExperience(10);
    onClose();
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h3 className="text-stabeon-text font-bold text-xl">What are you up to?</h3>
        <p className="text-stabeon-muted text-sm mt-1">This shows on the map and in the global feed.</p>
      </div>

      {/* Category grid */}
      <div className="grid grid-cols-5 gap-2">
        {ACTIVITY_CATEGORIES.map(cat => (
          <button
            key={cat.key}
            onClick={() => setCategory(cat.key)}
            className={`flex flex-col items-center py-3 rounded-xl text-xs gap-1 transition-all duration-150 ${
              category === cat.key ? 'pill-active' : 'pill-inactive'
            }`}
          >
            <span className="text-xl">{cat.emoji}</span>
            <span className="leading-tight text-center text-[10px]">{cat.label}</span>
          </button>
        ))}
      </div>

      {/* Free text */}
      <div>
        <div className="relative">
          <input
            value={text}
            onChange={e => handleTextChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && save()}
            placeholder="Add a note or paste a URL to auto-detect..."
            maxLength={80}
            className="input-field w-full pr-12"
          />
          <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono ${text.length > 65 ? 'text-stabeon-danger' : 'text-stabeon-muted/50'}`}>
            {text.length}/80
          </span>
        </div>
        {urlHint && (
          <p className="text-stabeon-accent text-xs mt-1.5 flex items-center gap-1">
            <span>✨</span> {urlHint}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <button onClick={save} className="btn-primary w-full">Update status</button>
        <button onClick={onClose} className="text-stabeon-muted text-sm py-1 hover:text-stabeon-text transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}
